import { type SiteId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { useLatest } from '@solinkcorp/solink-components';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useServiceDisplayInjection } from '../../../context/ServiceDisplayInjectionContext';
import type { LineEventSubtype, SdmEvent } from '../../../data-structures/sdmEventTypes';
import useRealZoneOptions, { type RealZoneOption } from '../../../hooks/useRealZoneOptions';
import type { ServiceDisplayEventsController } from '../../../ServiceDisplayEventsController';
import {
  buildDriveOffExit,
  buildLineCrossing,
  buildLineCrossingBounces,
  buildZoneEntry,
  buildZoneExit,
  newZoneContext,
  nextEntryId,
  randomDurationMs,
  type ZoneEventContext,
} from './eventBuilders';
import { injectSimulatedEvent } from './injectEvent';
import { createProgramRunner, PROGRAMS, type ProgramKey, type ProgramRunner } from './programs';

export interface TestingLogEntry {
  id: string;
  timestamp: number;
  summary: string;
  /** Either an SdmEvent or a program-lifecycle marker. Logged to console on click. */
  fullEvent: unknown;
  kind: 'event' | 'program';
  completed?: boolean;
}

interface ActiveProgram {
  key: ProgramKey;
  label: string;
  startedAt: number;
}

interface OpenEntry {
  id: string;
  tagIds: string[];
  ctx: ZoneEventContext;
  entryMs: number;
}

interface TestingTabProps {
  eventsController: ServiceDisplayEventsController;
  widgetTagIds: string[];
  tagNameById: ReadonlyMap<string, string>;
  testingLogs: TestingLogEntry[];
  onAppendLog: (entry: TestingLogEntry) => void;
}

const SHORT_RANGE_S: [number, number] = [2, 15];

const INJECT_TOGGLE_TITLE =
  'When ON, simulated events are POSTed to the device backend (interlink-ai) and flow ' +
  'through the real networking + database pipeline, returning over the live SSE stream. ' +
  'When OFF, events are processed client-side only. Requires the backend to have ' +
  'LINE_CROSSING_ENABLE_EVENT_INJECTION enabled and an AI-parser port configured.';

// Zone tracking dual events: entry + a scheduled exit a short random duration later. Every
// preset shares the same (2-15s) range — only `zoneType`/`driveOff` differ.
type DualPresetKey = 'queue' | 'driveOff' | 'dwell' | 'noActivity' | 'loitering';

const DUAL_PRESETS: Array<{
  key: DualPresetKey;
  label: string;
  zoneType: string;
  driveOff: boolean;
}> = [
  { key: 'queue', label: 'Queue', zoneType: 'queue', driveOff: false },
  { key: 'driveOff', label: 'Drive Off', zoneType: 'queue', driveOff: true },
  { key: 'dwell', label: 'Dwell', zoneType: 'dwell', driveOff: false },
  { key: 'noActivity', label: 'No Activity', zoneType: 'noActivity', driveOff: false },
  { key: 'loitering', label: 'Loitering', zoneType: 'loitering', driveOff: false },
];

// Line crossing single events.
type LineSinglePresetKey =
  'entranceAndExit' | 'entranceAndExitBounces' | 'passerbyTraffic' | 'floorLevelTransition';

const LINE_SINGLE_PRESETS: Array<{
  key: LineSinglePresetKey;
  label: string;
  subtype: LineEventSubtype;
  withBounces?: boolean;
}> = [
  { key: 'entranceAndExit', label: 'Entrance & Exit', subtype: 'entrance_and_exit' },
  {
    key: 'entranceAndExitBounces',
    label: 'Entrance & Exit (with bounces)',
    subtype: 'entrance_and_exit',
    withBounces: true,
  },
  { key: 'passerbyTraffic', label: 'Passerby traffic', subtype: 'passerby_traffic' },
  {
    key: 'floorLevelTransition',
    label: 'Floor level transition',
    subtype: 'floor_level_transition',
  },
];

type PastPresetKey =
  'shortZone30min' | 'shortZone90min' | 'shortDriveOff30min' | 'shortDriveOff90min';

const PAST_PRESETS: Array<{
  key: PastPresetKey;
  label: string;
  driveOff: boolean;
  agoMinutes: number;
}> = [
  { key: 'shortZone30min', label: 'Short queue zone (30min ago)', driveOff: false, agoMinutes: 30 },
  { key: 'shortZone90min', label: 'Short queue zone (90min ago)', driveOff: false, agoMinutes: 90 },
  {
    key: 'shortDriveOff30min',
    label: 'Short drive-off (30min ago)',
    driveOff: true,
    agoMinutes: 30,
  },
  {
    key: 'shortDriveOff90min',
    label: 'Short drive-off (90min ago)',
    driveOff: true,
    agoMinutes: 90,
  },
];

function makeLogId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function tagNamesForIds(tagIds: string[], tagNameById: ReadonlyMap<string, string>): string {
  const names = tagIds.map((id) => tagNameById.get(id) ?? id);
  return names.join(', ');
}

function summarizeEvent(
  event: SdmEvent,
  kindLabel: string,
  durationMs: number | null,
  tagNameById: ReadonlyMap<string, string>,
): string {
  const tags = tagNamesForIds(event.aiTagIds, tagNameById);
  const durationStr = durationMs == null ? '' : ` ${(durationMs / 1000).toFixed(1)}s •`;
  return `[${kindLabel}]${durationStr} Tags: ${tags}`;
}

interface ZoneSummaryInput {
  entryId: string;
  zoneType: string;
  phase: 'entry' | 'exit';
  driveOff?: boolean;
  zoneLabel: string;
  camId: string;
  tagIds: string[];
  durationMs?: number;
}

/**
 * Shared by the "Active entries" row header and the log line for that same entry/exit, so
 * the two are trivially easy to match up: entry id, zone type, entry-or-exit, zone label,
 * camera, and tags always appear in the same order and format in both places.
 */
function formatZoneSummary(
  input: ZoneSummaryInput,
  tagNameById: ReadonlyMap<string, string>,
): string {
  const tags = tagNamesForIds(input.tagIds, tagNameById);
  const phaseLabel = input.phase === 'exit' && input.driveOff ? 'exit (drive-off)' : input.phase;
  const durationStr = input.durationMs == null ? '' : ` · ${(input.durationMs / 1000).toFixed(1)}s`;
  return `${input.entryId} · ${input.zoneType} · ${phaseLabel} · ${input.zoneLabel} (${input.camId})${durationStr} · Tags: ${tags}`;
}

/**
 * Picks a (camera, zone) identity for a zone-tracking event: a real zone from the site's
 * line crossing configuration whose AI tags overlap `candidateTagIds`, if one exists, else a
 * fully simulated one. `zoneType` always reflects which preset triggered the event — it is
 * not read from the matched real zone's own configured type.
 */
function pickZoneContext(
  realZones: RealZoneOption[],
  candidateTagIds: string[],
  zoneType: string,
): ZoneEventContext {
  const matches = realZones.filter((zone) =>
    zone.aiTagIds.some((id) => candidateTagIds.includes(id)),
  );
  if (matches.length === 0) return newZoneContext(zoneType);
  const picked = matches[Math.floor(Math.random() * matches.length)];
  return newZoneContext(zoneType, picked);
}

export const TestingTab: React.FC<TestingTabProps> = ({
  eventsController,
  widgetTagIds,
  tagNameById,
  testingLogs,
  onAppendLog,
}) => {
  const [activeProgram, setActiveProgram] = useState<ActiveProgram | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  // All currently open zone entries (manual, dual-preset, or program-opened), each closeable
  // independently via its own row of close buttons — see the "Active entries" section below.
  const [openEntries, setOpenEntries] = useState<OpenEntry[]>([]);
  // True between a dual-event preset's entry submission and its scheduled exit. All buttons
  // are disabled during this window to prevent double-submission.
  const [dualEventPending, setDualEventPending] = useState(false);
  // 'ALL' attaches every configured widget tag to simulated events (previous behavior); a
  // specific tag id attaches only that tag. Reuses the same sub-tab look as the debugger's
  // AI tag filter tabs, but drives event content instead of filtering displayed events.
  const [testingTagScope, setTestingTagScope] = useState<string>('ALL');

  // Mirror props via refs so the runner's closures always see the freshest values.
  const tagNameByIdRef = useLatest(tagNameById);
  const onAppendLogRef = useLatest(onAppendLog);
  // `activeProgramRef` is kept in sync at both update sites (start/end) so the runner can
  // read the previous value without going through a setState updater — performing side
  // effects inside a setState updater triggers React's "setState while rendering another
  // component" warning, because updaters are invoked during reconciliation.
  const activeProgramRef = useRef<ActiveProgram | null>(null);
  // Tracks the in-flight dual-event preset so the close buttons can interrupt the
  // scheduled exit and resolve the entry early without firing it twice.
  const pendingDualRef = useRef<{
    timerHandle: ReturnType<typeof setTimeout>;
    entryId: string;
  } | null>(null);

  const hasTags = widgetTagIds.length > 0;

  // Reset back to 'ALL' if the previously-selected tag drops out of the layout (e.g. a
  // widget's tag was removed) so the scope never points at a stale/invalid tag id.
  useEffect(() => {
    if (testingTagScope !== 'ALL' && !widgetTagIds.includes(testingTagScope)) {
      setTestingTagScope('ALL');
    }
  }, [widgetTagIds, testingTagScope]);

  // The tag id(s) actually attached to the next simulated event — every configured widget
  // tag when scope is 'ALL', or just the selected one.
  const effectiveTagIds = testingTagScope === 'ALL' ? widgetTagIds : [testingTagScope];
  const effectiveTagIdsRef = useLatest(effectiveTagIds);

  // Real (non-simulated) zone-tracking zones from the site's line crossing configuration
  // (with multicam), used to prefer a real camera id + zone id over generated sim- ids when
  // one matches the currently selected AI tag scope.
  const [query] = useQueryParams();
  const siteId = (query.get('siteId') ?? undefined) as SiteId | undefined;
  const realZones = useRealZoneOptions(siteId);
  const realZonesRef = useLatest(realZones);

  // When enabled, simulated events are POSTed to the device's interlink-ai backend
  // (`/ai/modules/line_crossing/events/inject`) instead of being processed client-side.
  // They then flow through the real pipeline (networking + DB) and round-trip back over
  // the live SSE stream — exercising far more code than the client-only path.
  const [injectToBackend, setInjectToBackend] = useState(false);
  const { injectionBaseUrl } = useServiceDisplayInjection();
  const { accessToken } = useUserSession();
  const canInjectToBackend = Boolean(injectionBaseUrl) && Boolean(accessToken);
  const backendInjectionActive = injectToBackend && canInjectToBackend;

  // Single sink for every simulated event. Either injects to the backend or processes
  // locally, so call sites and the program runner don't need to know which mode is on.
  const emit = useCallback(
    (event: SdmEvent) => {
      if (backendInjectionActive && injectionBaseUrl) {
        void injectSimulatedEvent(injectionBaseUrl, accessToken, event).catch((error) => {
          eventsController.debugLog('processing_error', 'Backend event injection failed', {
            error: error instanceof Error ? error.message : String(error),
            event,
          });
        });
        return;
      }
      eventsController.processEvent(event);
    },
    [backendInjectionActive, injectionBaseUrl, accessToken, eventsController],
  );
  // The program runner is built once (below) and captures its deps; route through a ref
  // so a mid-session toggle of the injection mode is picked up by in-flight programs.
  const emitRef = useLatest(emit);

  // Live elapsed counter for the status banner.
  useEffect(() => {
    if (!activeProgram) {
      setElapsedSec(0);
      return;
    }
    const tick = () => setElapsedSec(Math.floor((Date.now() - activeProgram.startedAt) / 1000));
    tick();
    const handle = setInterval(tick, 1000);
    return () => clearInterval(handle);
  }, [activeProgram]);

  // Build the runner exactly once per controller. Closures read fresh values via refs.
  const runnerRef = useRef<ProgramRunner | null>(null);
  if (runnerRef.current === null) {
    runnerRef.current = createProgramRunner({
      emit: (event) => emitRef.current(event),
      getTagIds: () => effectiveTagIdsRef.current,
      createZoneContext: (tagIds, zoneType) =>
        pickZoneContext(realZonesRef.current, tagIds, zoneType),
      onEmitEvent: (event, completed, entryId) => {
        onAppendLogRef.current({
          id: makeLogId(),
          timestamp: Date.now(),
          summary: summarizeEmittedEvent(event, tagNameByIdRef.current, entryId),
          fullEvent: event,
          kind: 'event',
          completed,
        });
      },
      onOpenEntry: (entry) => {
        setOpenEntries((prev) => [
          ...prev,
          { id: entry.id, tagIds: entry.tagIds, ctx: entry.ctx, entryMs: entry.entryMs },
        ]);
      },
      onResolveEntry: (id) => {
        setOpenEntries((prev) => prev.filter((e) => e.id !== id));
      },
      onProgramEnd: (reason) => {
        const current = activeProgramRef.current;
        if (!current) return;
        activeProgramRef.current = null;
        setActiveProgram(null);
        const elapsed = ((Date.now() - current.startedAt) / 1000).toFixed(1);
        const marker = reason === 'completed' ? '■ Completed' : '⨯ Cancelled';
        onAppendLogRef.current({
          id: makeLogId(),
          timestamp: Date.now(),
          summary: `${marker} "${current.label}" after ${elapsed}s`,
          fullEvent: {
            kind: `program-${reason}`,
            program: current.label,
            elapsedSec: Number(elapsed),
          },
          kind: 'program',
        });
        eventsController.debugLog(
          'testing',
          `Program ${reason}: ${current.label} (after ${elapsed}s)`,
        );
      },
    });
  }

  // Cancel any running program on unmount so we don't leak timers.
  useEffect(() => {
    return () => {
      runnerRef.current?.cancel();
      if (pendingDualRef.current?.timerHandle) {
        clearTimeout(pendingDualRef.current.timerHandle);
      }
    };
  }, []);

  const submitLineCrossing = (key: LineSinglePresetKey) => {
    if (!hasTags) return;
    const preset = LINE_SINGLE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const now = Date.now();
    const event = buildLineCrossing(effectiveTagIds, now, {
      subtype: preset.subtype,
      bounces: preset.withBounces ? buildLineCrossingBounces(now) : undefined,
    });
    emit(event);
    onAppendLog({
      id: makeLogId(),
      timestamp: now,
      summary: summarizeEvent(event, preset.label, null, tagNameById),
      fullEvent: event,
      kind: 'event',
    });
  };

  const submitActiveEntry = () => {
    if (!hasTags) return;
    const now = Date.now();
    const tagIds = effectiveTagIds;
    const ctx = pickZoneContext(realZones, tagIds, 'dwell');
    const id = nextEntryId();
    const event = buildZoneEntry(tagIds, now, ctx);
    emit(event);
    setOpenEntries((prev) => [...prev, { id, tagIds, ctx, entryMs: now }]);
    onAppendLog({
      id: makeLogId(),
      timestamp: now,
      summary: formatZoneSummary(
        {
          entryId: id,
          zoneType: ctx.zoneType,
          phase: 'entry',
          zoneLabel: ctx.label,
          camId: ctx.camId,
          tagIds,
        },
        tagNameById,
      ),
      fullEvent: event,
      kind: 'event',
    });
  };

  const closeActiveEntry = (entryId: string, asDriveOff: boolean) => {
    const entry = openEntries.find((e) => e.id === entryId);
    if (!entry) return;
    // If this entry belongs to an in-flight dual-event preset, cancel its scheduled
    // exit so it doesn't fire a second time, and unlock the rest of the UI.
    if (pendingDualRef.current && pendingDualRef.current.entryId === entry.id) {
      clearTimeout(pendingDualRef.current.timerHandle);
      pendingDualRef.current = null;
      setDualEventPending(false);
    }
    setOpenEntries((prev) => prev.filter((e) => e.id !== entry.id));
    const exitMs = Date.now();
    const exit = asDriveOff
      ? buildDriveOffExit(entry.tagIds, entry.entryMs, exitMs, entry.ctx)
      : buildZoneExit(entry.tagIds, entry.entryMs, exitMs, entry.ctx);
    emit(exit);
    onAppendLog({
      id: makeLogId(),
      timestamp: exitMs,
      summary: formatZoneSummary(
        {
          entryId: entry.id,
          zoneType: entry.ctx.zoneType,
          phase: 'exit',
          driveOff: asDriveOff,
          zoneLabel: entry.ctx.label,
          camId: entry.ctx.camId,
          tagIds: entry.tagIds,
          durationMs: exitMs - entry.entryMs,
        },
        tagNameById,
      ),
      fullEvent: exit,
      kind: 'event',
      completed: true,
    });
  };

  const submitDualEvent = (key: DualPresetKey) => {
    if (!hasTags) return;
    const preset = DUAL_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const tagIds = effectiveTagIds;
    const now = Date.now();
    const durationMs = randomDurationMs(SHORT_RANGE_S[0], SHORT_RANGE_S[1]);

    const ctx = pickZoneContext(realZones, tagIds, preset.zoneType);
    const entryId = nextEntryId();
    const entry = buildZoneEntry(tagIds, now, ctx);
    setDualEventPending(true);
    setOpenEntries((prev) => [...prev, { id: entryId, tagIds, ctx, entryMs: now }]);
    emit(entry);
    onAppendLog({
      id: makeLogId(),
      timestamp: now,
      summary: formatZoneSummary(
        {
          entryId,
          zoneType: ctx.zoneType,
          phase: 'entry',
          zoneLabel: ctx.label,
          camId: ctx.camId,
          tagIds,
        },
        tagNameById,
      ),
      fullEvent: entry,
      kind: 'event',
    });

    // Schedule the natural exit. The close buttons can also interrupt this timer via
    // `pendingDualRef` — see `closeActiveEntry` above. While this timer is alive every
    // button except Close stays disabled (via `dualEventPending`).
    const timerHandle = setTimeout(() => {
      const exitMs = Date.now();
      const exit = preset.driveOff
        ? buildDriveOffExit(tagIds, now, exitMs, ctx)
        : buildZoneExit(tagIds, now, exitMs, ctx);
      emit(exit);
      onAppendLog({
        id: makeLogId(),
        timestamp: exitMs,
        summary: formatZoneSummary(
          {
            entryId,
            zoneType: ctx.zoneType,
            phase: 'exit',
            driveOff: preset.driveOff,
            zoneLabel: ctx.label,
            camId: ctx.camId,
            tagIds,
            durationMs,
          },
          tagNameById,
        ),
        fullEvent: exit,
        kind: 'event',
        completed: true,
      });
      setOpenEntries((prev) => prev.filter((e) => e.id !== entryId));
      pendingDualRef.current = null;
      setDualEventPending(false);
    }, durationMs);
    pendingDualRef.current = { timerHandle, entryId };
  };

  const submitPastEvent = (key: PastPresetKey) => {
    if (!hasTags) return;
    const preset = PAST_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const tagIds = effectiveTagIds;
    const exitMs = Date.now() - preset.agoMinutes * 60_000;
    const durationMs = randomDurationMs(SHORT_RANGE_S[0], SHORT_RANGE_S[1]);
    const entryMs = exitMs - durationMs;
    const ctx = pickZoneContext(realZones, tagIds, 'queue');
    const kindLabel = preset.driveOff ? 'drive-off' : 'short zone';

    const entry = buildZoneEntry(tagIds, entryMs, ctx);
    emit(entry);
    onAppendLog({
      id: makeLogId(),
      timestamp: entryMs,
      summary: summarizeEvent(
        entry,
        `${kindLabel} (entry, ${preset.agoMinutes}min ago)`,
        null,
        tagNameById,
      ),
      fullEvent: entry,
      kind: 'event',
    });

    const exit = preset.driveOff
      ? buildDriveOffExit(tagIds, entryMs, exitMs, ctx)
      : buildZoneExit(tagIds, entryMs, exitMs, ctx);
    emit(exit);
    onAppendLog({
      id: makeLogId(),
      timestamp: exitMs,
      summary: summarizeEvent(
        exit,
        `${kindLabel} (${preset.agoMinutes}min ago)`,
        durationMs,
        tagNameById,
      ),
      fullEvent: exit,
      kind: 'event',
    });
  };

  const startProgram = (key: ProgramKey, label: string) => {
    if (!hasTags) return;
    if (activeProgramRef.current !== null) return;
    const now = Date.now();
    const program = { key, label, startedAt: now };
    activeProgramRef.current = program;
    setActiveProgram(program);
    onAppendLog({
      id: makeLogId(),
      timestamp: now,
      summary: `▶ Started "${label}"`,
      fullEvent: { kind: 'program-start', program: label },
      kind: 'program',
    });
    eventsController.debugLog('testing', `Program started: ${label}`);
    runnerRef.current?.start(key);
  };

  const cancelProgram = () => {
    runnerRef.current?.cancel();
  };

  const onLogClick = (entry: TestingLogEntry) => {
    // eslint-disable-next-line no-console
    console.log('[SDM testing event]', entry.fullEvent);
  };

  const openCount = openEntries.length;
  const filteredOpenEntries = openEntries.filter(
    (entry) => testingTagScope === 'ALL' || entry.tagIds.includes(testingTagScope),
  );
  // Common gates: no tags, a program is running, or a dual-event preset is mid-flight
  // (entry submitted, exit pending) — in all three cases the entire UI is locked to
  // prevent races / double-submission.
  const baseDisabled = !hasTags || activeProgram !== null || dualEventPending;
  // Line crossing stays available while there are open entries — the spec only requires
  // dual events and programs to be disabled in that case.
  const singleDisabled = baseDisabled;
  // Manual active entries can be opened repeatedly — no cap on how many can be open at once.
  const activeEntryDisabled = baseDisabled;
  // Dual events / programs still require every open entry to be closed first, to keep those
  // more complex flows easy to reason about.
  const dualDisabled = baseDisabled || openCount > 0;
  const programDisabled = baseDisabled || openCount > 0;
  // Each entry's own close row stays available even mid-`dualEventPending` so the user can
  // resolve that entry early (and cancel its pending exit timer); see `closeActiveEntry`.
  const entryRowDisabled = !hasTags || activeProgram !== null;

  return (
    <div className="testing-tab">
      {activeProgram && (
        <div className="testing-program-banner">
          <span>
            ▶ Running: <strong>{activeProgram.label}</strong>
          </span>
          <span className="testing-elapsed">{elapsedSec}s elapsed</span>
          <button className="debugger-btn" onClick={cancelProgram} title="Interrupt program">
            Interrupt
          </button>
        </div>
      )}

      {!hasTags && (
        <div className="testing-empty-message">
          Configure at least one widget with an AI tag to simulate events.
        </div>
      )}

      {hasTags && (
        <div className="testing-section">
          <div className="testing-section-title">Simulated AI tag(s)</div>
          <div className="debugger-tabs ai-tag-tabs">
            <button
              className={cn('debugger-tab', testingTagScope === 'ALL' && 'active')}
              onClick={() => setTestingTagScope('ALL')}
              title="Attach every configured widget tag to simulated events"
            >
              ALL
              <span className="tab-count">({openEntries.length})</span>
            </button>
            {widgetTagIds.map((tagId) => {
              const displayName = tagNameById.get(tagId) ?? tagId;
              const count = openEntries.filter((entry) => entry.tagIds.includes(tagId)).length;
              return (
                <button
                  key={tagId}
                  className={cn('debugger-tab', testingTagScope === tagId && 'active')}
                  onClick={() => setTestingTagScope(tagId)}
                  title={`Attach only "${displayName}" to simulated events`}
                >
                  {displayName}
                  <span className="tab-count">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="testing-section">
        <div className="testing-section-title">Destination</div>
        <label className="testing-inject-toggle" title={INJECT_TOGGLE_TITLE}>
          <input
            type="checkbox"
            checked={injectToBackend}
            disabled={!canInjectToBackend || activeProgram !== null}
            onChange={(e) => setInjectToBackend(e.target.checked)}
          />
          <span>
            Inject into backend pipeline
            {backendInjectionActive
              ? ' — ON (real SSE + DB round-trip)'
              : canInjectToBackend
                ? ' — OFF (processed client-side only)'
                : ' — unavailable (no device address)'}
          </span>
        </label>
      </div>

      <div className="testing-section">
        <div className="testing-section-title">Single events — line crossing</div>
        <div className="testing-button-row">
          {LINE_SINGLE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              className="testing-preset-btn"
              disabled={singleDisabled}
              onClick={() => submitLineCrossing(preset.key)}
              title={`Submit a ${preset.label} event`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="testing-section">
        <div className="testing-section-title">Single events — zone tracking</div>
        <div className="testing-button-row">
          <button
            className="testing-preset-btn"
            disabled={activeEntryDisabled}
            onClick={submitActiveEntry}
            title="Open a new zone entry that stays active until you close it — open as many as you like"
          >
            Active dwell entry (no exit)
          </button>
          {PAST_PRESETS.map((preset) => (
            <button
              key={preset.key}
              className="testing-preset-btn"
              disabled={singleDisabled}
              onClick={() => submitPastEvent(preset.key)}
              title={`Submit a closed ${preset.driveOff ? 'drive-off' : 'short zone'} event ending ${preset.agoMinutes} minutes in the past`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {filteredOpenEntries.length > 0 && (
        <div className="testing-section">
          <div className="testing-section-title">Active entries ({filteredOpenEntries.length})</div>
          <div className="testing-entries-list">
            {filteredOpenEntries.map((entry) => (
              <div key={entry.id} className="testing-entry-row">
                <div className="testing-entry-label">
                  {formatZoneSummary(
                    {
                      entryId: entry.id,
                      zoneType: entry.ctx.zoneType,
                      phase: 'entry',
                      zoneLabel: entry.ctx.label,
                      camId: entry.ctx.camId,
                      tagIds: entry.tagIds,
                    },
                    tagNameById,
                  )}
                  {entry.ctx.isSimulated ? '' : ' — real zone'}
                </div>
                <div className="testing-button-row">
                  <button
                    className="testing-preset-btn"
                    disabled={entryRowDisabled}
                    onClick={() => closeActiveEntry(entry.id, false)}
                    title={`Emit a normal exit for ${entry.id}`}
                  >
                    Close {entry.id}
                  </button>
                  <button
                    className="testing-preset-btn"
                    disabled={entryRowDisabled}
                    onClick={() => closeActiveEntry(entry.id, true)}
                    title={`Emit a drive-off exit for ${entry.id}`}
                  >
                    Close {entry.id} as drive-off
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="testing-section">
        <div className="testing-section-title">Dual events (zone tracking, short 2-15s)</div>
        <div className="testing-button-row">
          {DUAL_PRESETS.map((preset) => (
            <button
              key={preset.key}
              className="testing-preset-btn"
              disabled={dualDisabled}
              onClick={() => submitDualEvent(preset.key)}
              title={`Submit a ${preset.label} event`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="testing-section">
        <div className="testing-section-title">Programs</div>
        <div className="testing-button-row">
          {PROGRAMS.map((program) => (
            <button
              key={program.key}
              className="testing-preset-btn"
              disabled={programDisabled}
              onClick={() => startProgram(program.key, program.label)}
              title={`Run program: ${program.label}`}
            >
              {program.label}
            </button>
          ))}
        </div>
      </div>

      <div className="testing-section">
        <div className="testing-section-title">Log ({testingLogs.length})</div>
        {testingLogs.length === 0 ? (
          <div className="no-events">No testing events submitted yet.</div>
        ) : (
          <div className="testing-log-list">
            {[...testingLogs].reverse().map((entry) => (
              <div
                key={entry.id}
                className={cn(
                  'debug-event testing-log-entry',
                  entry.kind === 'program' && 'program-entry',
                  entry.completed && 'completed-entry',
                )}
                onClick={() => onLogClick(entry)}
                title="Click to print full event to browser console"
              >
                <div className="event-header">
                  <span className="event-time">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="event-message">{entry.summary}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function summarizeEmittedEvent(
  event: SdmEvent,
  tagNameById: ReadonlyMap<string, string>,
  entryId?: string,
): string {
  if (event.evt_type === 'line') {
    return summarizeEvent(event, 'line', null, tagNameById);
  }
  // zone event — same format as the "Active entries" row header (see `formatZoneSummary`) so
  // program-opened entries are just as easy to match against their log lines.
  const isEntry = event.exit_ms == null;
  return formatZoneSummary(
    {
      entryId: entryId ?? '?',
      zoneType: event.metadata.zoneType,
      phase: isEntry ? 'entry' : 'exit',
      driveOff: event.drive_off === true,
      zoneLabel: event.label,
      camId: event.cam_id,
      tagIds: event.aiTagIds,
      durationMs: isEntry ? undefined : event.exit_ms! - event.entry_ms,
    },
    tagNameById,
  );
}
